import type GeoHierarchy from '@/models/GeoHierarchy';

import usGeoHierarchy from '../constants/UsGeoHierarchy.json';

export type Option = {
  value: string;
  label: string;
  flag?: string;
};

const ISO_COUNTRIES = [
  // eslint-disable-next-line prettier/prettier
  'AF', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AQ', 'AG', 'AR', 'AM', 'AW', 'AU', 'AT', 'AZ', 'BS', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BM', 'BT', 'BO', 'BQ', 'BA', 'BW', 'BR', 'IO', 'BN', 'BG', 'BF', 'BI', 'KH', 'CM', 'CA', 'CV', 'KY', 'CF', 'TD', 'CL', 'CN', 'CX', 'CC', 'CO', 'KM', 'CG', 'CD', 'CK', 'CR', 'CI', 'HR', 'CU', 'CW', 'CY', 'CZ', 'DK', 'DJ', 'DM', 'DO', 'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'SZ', 'ET', 'FK', 'FO', 'FJ', 'FI', 'FR', 'GF', 'PF', 'TF', 'GA', 'GM', 'GE', 'DE', 'GH', 'GI', 'GR', 'GL', 'GD', 'GP', 'GU', 'GT', 'GG', 'GN', 'GW', 'GY', 'HT', 'HM', 'VA', 'HN', 'HK', 'HU', 'IS', 'IN', 'ID', 'IR', 'IQ', 'IE', 'IM', 'IL', 'IT', 'JM', 'JP', 'JE', 'JO', 'KZ', 'KE', 'KI', 'KP', 'KR', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR', 'LY', 'LI', 'LT', 'LU', 'MO', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT', 'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'FM', 'MD', 'MC', 'MN', 'ME', 'MS', 'MA', 'MZ', 'MM', 'NA', 'NR', 'NP', 'NL', 'NC', 'NZ', 'NI', 'NE', 'NG', 'NU', 'NF', 'MK', 'MP', 'NO', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE', 'PH', 'PN', 'PL', 'PT', 'PR', 'QA', 'RE', 'RO', 'RU', 'RW', 'BL', 'SH', 'KN', 'LC', 'MF', 'PM', 'VC', 'WS', 'SM', 'ST', 'SA', 'SN', 'RS', 'SC', 'SL', 'SG', 'SX', 'SK', 'SI', 'SB', 'SO', 'ZA', 'GS', 'SS', 'ES', 'LK', 'SD', 'SR', 'SJ', 'SE', 'CH', 'SY', 'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TK', 'TO', 'TT', 'TN', 'TR', 'TM', 'TC', 'TV', 'UG', 'UA', 'AE', 'GB', 'US', 'UM', 'UY', 'UZ', 'VU', 'VE', 'VN', 'VG', 'VI', 'WF', 'EH', 'YE', 'ZM', 'ZW'
];

const regionNames = new Intl.DisplayNames(
  ['en'], // or 'pt-PT', 'fr', etc.
  { type: 'region' }
);

export const countryOptions = ISO_COUNTRIES.map((value) => ({
  value,
  label: regionNames.of(value),
  flag: String.fromCodePoint(...value.split('').map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)),
}));

export const stateOptions: Option[] = [];
export const dmaOptions: Option[] = [];
export const cityOptions: Option[] = [];

// lookup maps for efficient label retrieval
export const dmaLabelLookup = new Map<string, string>(); // dmaValue -> DMA Label (DMA Code)
export const cityPostalCodeMap = new Map<string, string[]>(); // cityValue → zip code values
export const cityLabelMap = new Map<string, string>(); // cityValue → "Los Angeles, CA"

(usGeoHierarchy as GeoHierarchy)[2].forEach((subdivision) => {
  const stateName = subdivision[0];
  const stateCode = subdivision[1];

  if (stateName) {
    stateOptions.push({ value: `US-${stateCode}`, label: stateName });
  }

  subdivision[2].forEach((dma) => {
    const [dmaName, dmaCode, cities] = dma;

    // Skip synthetic DMAs (territories with no real DMA)
    if (dmaCode) {
      const dmaValue = `US-${stateCode}-DMA_${dmaCode}`;
      const dmaLabel = `${dmaName} (DMA ${dmaCode})`;
      dmaOptions.push({ value: dmaValue, label: dmaLabel });
      dmaLabelLookup.set(dmaValue, dmaLabel);
    }

    cities.forEach((city) => {
      const [cityName, zipCodes] = city;
      const cityKey = cityName.toUpperCase().replace(/\s+/g, '_');
      const cityValue = `US-${stateCode}-${cityKey}`;
      cityOptions.push({
        value: cityValue,
        label: cityName,
      });

      if (!cityPostalCodeMap.has(cityValue)) {
        cityPostalCodeMap.set(cityValue, []);
        cityLabelMap.set(cityValue, cityName);
      }
      const cityZips = cityPostalCodeMap.get(cityValue)!;

      zipCodes.forEach((zip) => {
        const zipValue = `US-${stateCode}-${zip}`;
        cityZips.push(zipValue);
      });
    });
  });
});

export const getCountryName = (code: string): string | undefined => {
  return countryOptions.find((option) => option.value === code)?.label;
};

export const getStateName = (code: string): string | undefined => {
  return stateOptions.find((option) => option.value === code)?.label;
};

// export const resolveLocationPart = (input: string): { type: string; value: string } | null => {
//   const trimmed = input.trim();
//   if (!trimmed) {
//     return null;
//   }

//   const lower = trimmed.toLowerCase();

//   // Name-based lookups (case insensitive)
//   const countryMatch = countryOptions.find((o) => o.label?.toLowerCase() === lower);
//   if (countryMatch) {
//     return { type: 'Country', value: countryMatch.value };
//   }

//   const stateMatch = stateOptions.find((o) => o.label.toLowerCase() === lower);
//   if (stateMatch) {
//     return { type: 'Subdivision', value: stateMatch.value };
//   }

//   const cityMatch = cityOptions.find((o) => o.label.split(',')[0].toLowerCase() === lower);
//   if (cityMatch) {
//     return { type: 'City', value: cityMatch.value };
//   }

//   const dmaMatch = dmaOptions.find((o) => o.label.toLowerCase() === lower);
//   if (dmaMatch) {
//     return { type: 'Dma', value: dmaMatch.value };
//   }

//   // Code-based fallback (case insensitive)
//   const upper = trimmed.toUpperCase();
//   const segments = upper.split('-');

//   if (segments.length >= 3) {
//     const thirdSegment = segments.slice(2).join('-');
//     if (thirdSegment.startsWith('DMA_')) {
//       return { type: 'Dma', value: upper };
//     }
//     if (/^\d+$/.test(thirdSegment)) {
//       return { type: 'PostalCode', value: upper };
//     }
//     return { type: 'City', value: upper };
//   }
//   if (segments.length === 2) {
//     return { type: 'Subdivision', value: upper };
//   }
//   if (segments.length === 1 && upper.length === 2) {
//     return { type: 'Country', value: upper };
//   }

//   return null;
// };

export const getLocationLabel = (value: string, type: string): string => {
  if (type === 'Country') {
    return getCountryName(value) || value;
  }
  if (type === 'Subdivision') {
    return getStateName(value) || value;
  }
  if (type === 'Dma') {
    const dmaLabel = dmaLabelLookup.get(value);
    if (dmaLabel) {
      return dmaLabel;
    }
    // Format: US-NY-DMA_501 → DMA 501 (NY, US)
    const parts = value.split('-');
    if (parts.length >= 3) {
      const dmaCode = parts.slice(2).join('-').replace(/_/g, ' ');
      return `${dmaCode}`;
    }
    return value;
  }
  if (type === 'City') {
    // Format: US-CA-SAN_FRANCISCO → San Francisco (CA, US)
    const parts = value.split('-');
    if (parts.length >= 3) {
      const cityName = parts.slice(2).join('-').replace(/_/g, ' ');
      const formatted = cityName.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
      return `${formatted}`;
    }
    return value.replace(/_/g, ' ');
  }
  if (type === 'PostalCode') {
    // Format: US-NY-10001 → 10001 (NY, US)
    const parts = value.split('-');
    if (parts.length >= 3) {
      const zipCode = parts.slice(2).join('-');
      return `${zipCode}`;
    }
  }
  return value;
};

export const getLocationBreadcrumb = (value: string): string | null => {
  const parts = value.split('-');
  const countryCode = parts[0];
  const countryName = getCountryName(countryCode) || countryCode;
  const stateCode = parts.length >= 2 ? parts.slice(0, 2).join('-') : '';
  const stateName = stateCode ? getStateName(stateCode) || parts[1] : '';

  return [stateName, countryName].filter(Boolean).join(', ');
};
